import { faker } from '@faker-js/faker'
import type { Product, ProductFilters, Category } from '../types/product'
import { createProduct } from './factories/product'
import { CATEGORIES } from './seeds/categories'

class MockDatabase {
  private products: Product[] = []
  private initialized = false

  async init(productCount = 500): Promise<void> {
    if (this.initialized) return

    faker.seed(12345) // Reproducible data
    this.products = Array.from({ length: productCount }, () => createProduct())
    this.initialized = true
  }

  getProducts(filters?: ProductFilters): Product[] {
    let filtered = [...this.products]

    // Apply filters
    if (filters?.category) {
      filtered = filtered.filter(p => p.category_id === filters.category)
    }

    if (filters?.minPrice !== undefined) {
      filtered = filtered.filter(p => p.pricing.current_price >= filters.minPrice!)
    }

    if (filters?.maxPrice !== undefined) {
      filtered = filtered.filter(p => p.pricing.current_price <= filters.maxPrice!)
    }

    if (filters?.minRating !== undefined) {
      filtered = filtered.filter(p => p.rating.average >= filters.minRating!)
    }

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(
        p =>
          p.title.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
      )
    }

    if (filters?.freeShipping) {
      filtered = filtered.filter(p => p.shipping.free_shipping)
    }

    if (filters?.inStockOnly) {
      filtered = filtered.filter(p => p.stock_status === 'in_stock')
    }

    if (filters?.vendors && filters.vendors.length > 0) {
      filtered = filtered.filter(p => filters.vendors!.includes(p.vendor.id))
    }

    if (filters?.location) {
      filtered = filtered.filter(p => p.vendor.location === filters.location)
    }

    // Apply sorting
    const sort = filters?.sort || 'relevance'
    switch (sort) {
      case 'price_asc':
        filtered.sort((a, b) => a.pricing.current_price - b.pricing.current_price)
        break
      case 'price_desc':
        filtered.sort((a, b) => b.pricing.current_price - a.pricing.current_price)
        break
      case 'rating_desc':
        filtered.sort((a, b) => b.rating.average - a.rating.average)
        break
      case 'created_desc':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        break
      case 'discount_desc':
        filtered.sort((a, b) => b.pricing.discount_percentage - a.pricing.discount_percentage)
        break
      case 'sales_desc':
        filtered.sort((a, b) => b.rating.count - a.rating.count)
        break
    }

    // Apply pagination
    const page = filters?.page || 1
    const limit = filters?.limit || 20
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit

    return filtered.slice(startIndex, endIndex)
  }

  getProductById(id: string): Product | undefined {
    return this.products.find(p => p.id === id)
  }

  getProductBySlug(slug: string): Product | undefined {
    return this.products.find(p => p.slug === slug)
  }

  getCategories(): Category[] {
    return CATEGORIES
  }

  getTodayDeals(): Product[] {
    return this.products
      .filter(p => p.pricing.discount_percentage >= 30)
      .sort((a, b) => b.pricing.discount_percentage - a.pricing.discount_percentage)
      .slice(0, 12)
  }

  searchProducts(query: string, limit = 10): Product[] {
    const searchLower = query.toLowerCase()
    return this.products
      .filter(
        p =>
          p.title.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
      )
      .slice(0, limit)
  }

  getTotalCount(filters?: ProductFilters): number {
    let filtered = [...this.products]

    if (filters?.category) {
      filtered = filtered.filter(p => p.category_id === filters.category)
    }

    if (filters?.minPrice !== undefined) {
      filtered = filtered.filter(p => p.pricing.current_price >= filters.minPrice!)
    }

    if (filters?.maxPrice !== undefined) {
      filtered = filtered.filter(p => p.pricing.current_price <= filters.maxPrice!)
    }

    if (filters?.minRating !== undefined) {
      filtered = filtered.filter(p => p.rating.average >= filters.minRating!)
    }

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(
        p =>
          p.title.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
      )
    }

    return filtered.length
  }
}

export const mockDb = new MockDatabase()
